import { HgSupportedLanguage } from '@smg-real-estate/domus';
import {
  Attachment,
  AttachmentType,
  L10N,
  Listing,
  Url,
  UrlType,
} from '@smg-real-estate/hg-rets-schema';
import { md5 } from 'js-md5';
import clone from 'lodash.clone';

function getPrimaryLocaleKey(
  localization: NonNullable<Listing['localization']>,
): HgSupportedLanguage {
  return (localization.primary ?? HgSupportedLanguage.DE) as HgSupportedLanguage;
}

function readPrimaryLocaleBucket(listing: Listing): L10N | undefined {
  const localization = listing.localization;
  if (!localization) {
    return undefined;
  }

  return localization[getPrimaryLocaleKey(localization)];
}

export function getListingDefaultLocale(listing: Listing): L10N | undefined {
  return readPrimaryLocaleBucket(listing);
}

/** Ensures localization, primary, and primary bucket exist; returns the writable primary bucket. */
export function ensureListingDefaultLocale(
  listing: Listing,
  fallbackPrimary: HgSupportedLanguage = HgSupportedLanguage.DE,
): L10N {
  if (!listing.localization) {
    listing.localization = { primary: fallbackPrimary };
  }

  if (!listing.localization.primary) {
    listing.localization.primary = fallbackPrimary;
  }

  const localeKey = getPrimaryLocaleKey(listing.localization);
  if (!listing.localization[localeKey]) {
    listing.localization[localeKey] = {};
  }

  return listing.localization[localeKey]!;
}

export function getListingLocale(
  listing: Listing,
  language?: HgSupportedLanguage | string,
): L10N | undefined {
  const localizedListing = listing.localization?.[language as HgSupportedLanguage];

  return localizedListing ?? getListingDefaultLocale(listing);
}

export function getListingWebsiteLink(listingUrls: Url[] = []): Url | undefined {
  return listingUrls.find((url: Url) => url.type === UrlType.Link);
}

const updateWebsiteLinkData = (
  listingUrls: Url[],
  {
    shouldSetValue,
    shouldSetTitle,
    linkData,
    existingData,
  }: {
    shouldSetValue: boolean;
    shouldSetTitle: boolean;
    linkData: Url;
    existingData: Url;
  },
): Url[] => {
  if (shouldSetValue && shouldSetTitle) {
    listingUrls.push({
      type: UrlType.Link,
      value: linkData.value,
      title: linkData.title,
    });
  } else if (shouldSetValue) {
    // Update "value" only
    listingUrls.push({
      type: UrlType.Link,
      value: linkData.value,
      title: existingData.title,
    });
  } else if (shouldSetTitle) {
    // Update "title" only
    listingUrls.push({
      type: UrlType.Link,
      value: existingData.value,
      title: linkData.title,
    });
  }
  return listingUrls;
};

const addWebsiteLinkData = (listingUrls: Url[], { linkData }: { linkData: Url }): Url[] => {
  if (linkData.value || linkData.title) {
    listingUrls.push({
      type: UrlType.Link,
      value: linkData.value,
      title: linkData.title,
    });
  }
  return listingUrls;
};

// eslint-disable-next-line complexity
export function setListingWebsiteLink(listingUrls: Url[], urlData: Url): Url[] {
  const clonedUrls = !listingUrls ? [] : clone(listingUrls);
  const cleanedUrls = clonedUrls.filter(
    (listingUrl) => listingUrl.type && listingUrl.type !== UrlType.Link,
  );

  const shouldSetValue = 'value' in urlData;
  const shouldSetTitle = 'title' in urlData;

  // A value key must be present
  if (!shouldSetValue && !shouldSetTitle) {
    return cleanedUrls;
  }

  // Prepare (parse)
  const linkData = {
    value: urlData?.value?.trim(),
    title: urlData?.title,
  };

  // Both keys present, both values are empty. Means it will be removed.
  if (shouldSetValue && !linkData.value && shouldSetTitle && !linkData.title) {
    return cleanedUrls;
  }

  // We need to search for an existing entry here because this entry has multiple values.
  const existingData = getListingWebsiteLink(clonedUrls);

  // Not yet present, add it.
  if (!existingData) {
    return addWebsiteLinkData(cleanedUrls, { linkData });
  }

  // Passed empty values - No existing values. Means it will be removed.
  if (
    (shouldSetValue && !linkData.value && !existingData.title) ||
    (shouldSetTitle && !linkData.title && !existingData.value)
  ) {
    return cleanedUrls;
  }

  // Finally, update and return.
  return updateWebsiteLinkData(cleanedUrls, {
    shouldSetTitle,
    shouldSetValue,
    linkData,
    existingData,
  });
}

export function getListingYoutubeLink(listingUrls: Url[] = []): Url | undefined {
  return listingUrls.find((url: Url) => url.type === UrlType.Youtube);
}

export function setListingYoutubeLink(listingUrls: Url[], inputUrl: string): Url[] {
  const clonedUrls = !listingUrls ? [] : clone(listingUrls);
  const cleanedUrls = clonedUrls.filter(
    (listingUrl) => listingUrl.type && listingUrl.type !== UrlType.Youtube,
  );

  const url = inputUrl?.trim();
  if (!url) {
    return cleanedUrls;
  }

  cleanedUrls.push({
    type: UrlType.Youtube,
    value: url,
  });

  return cleanedUrls;
}

export function getVirtualTourLink(listingUrls: Url[] = []): Url | undefined {
  return listingUrls.find(
    (url: Url) => url.type === UrlType.VirtualTour || url.type === UrlType.Archilogic,
  );
}

export function setVirtualTourLink(listingUrls: Url[], inputUrl: string): Url[] {
  const clonedUrls = !listingUrls ? [] : clone(listingUrls);
  const cleanedUrls = clonedUrls.filter(
    (listingUrl) =>
      listingUrl.type &&
      listingUrl.type !== UrlType.VirtualTour &&
      listingUrl.type !== UrlType.Archilogic,
  );

  const url = inputUrl?.trim();
  if (!url) {
    return cleanedUrls;
  }

  cleanedUrls.push({
    type: UrlType.VirtualTour,
    value: url,
  });

  return cleanedUrls;
}

export function getListingImages(
  listing: Listing,
  language?: HgSupportedLanguage | string,
): Attachment[] | undefined {
  const localizedImages = getListingLocale(listing, language)?.attachments?.filter(
    (attachment) => attachment.type === AttachmentType.Image,
  );

  if (localizedImages?.length || !language) {
    return localizedImages;
  }

  return getListingDefaultLocale(listing)?.attachments?.filter(
    (attachment) => attachment.type === AttachmentType.Image,
  );
}

export function getListingImage(
  listing: Listing,
  index: number,
  language?: HgSupportedLanguage | string,
): Attachment | undefined {
  return getListingImages(listing, language)?.[index];
}

const ROTATE_ANGLES_REGEX = /\/a_(0|90|180|270)\//;
const ROTATE_FILTER_MAP: Record<number, string> = {
  0: 'a_0',
  90: 'a_90',
  180: 'a_180',
  270: 'a_270',
};

export const isImageFilterSupported = (urlHref: string): boolean => {
  /**
   * This function is to ensure that the images come from a source that supports our image filters.
   * We check if the url contains "homegate" (e.g. https://media.homegate.ch/path/to/image.jpg).
   */
  return urlHref.includes('homegate.ch');
};

export const getRotatedAngleByFilter = (urlHref = ''): number => {
  const matches = urlHref.match(ROTATE_ANGLES_REGEX);
  return matches ? parseInt(matches[1]) : 0;
};

export const getRotatedImageUrl = (rotationAngle: number, urlHref = ''): string => {
  // Remove existing filter
  const currentUrlHref = urlHref.replace(ROTATE_ANGLES_REGEX, '/');
  // Get filter by angle
  const rotationFilter = ROTATE_FILTER_MAP[rotationAngle] || ROTATE_FILTER_MAP[0];

  return getUrlForTransformation(rotationFilter, currentUrlHref, false);
};

export const getLargeImageUrl = (urlHref = ''): string => {
  return getUrlForTransformation('t_web_dp_large', urlHref, false);
};

export const getMediumImageUrl = (urlHref = ''): string => {
  return getUrlForTransformation('t_listing_width_580', urlHref, true);
};

export const getPreviewUrl = (urlHref = ''): string => {
  return getUrlForTransformation('t_listing_draft_thumbnail', urlHref, true);
};

const getUrlForTransformation = (
  transformation: string,
  urlHref = '',
  isPreview = true,
): string => {
  if (!isImageFilterSupported(urlHref)) {
    return urlHref;
  }

  // Create new url
  const currentUrlObject = new URL(urlHref);
  // Cleanup pathname (remove deprecated paths)
  const currentUrlPath = currentUrlObject.pathname
    .replace('/f_auto/', '/')
    .replace(/^\/((image\/)?upload)+\//, '/');

  if (isPreview && currentUrlPath.includes('/a_')) {
    currentUrlObject.pathname = currentUrlPath.replace('/a_', `/${transformation}_a_`);
  } else {
    currentUrlObject.pathname = `/${transformation}${currentUrlPath}`;
  }

  return currentUrlObject.href;
};

export function generateFileHash(name: string, lastModified: number | undefined): string {
  if (!lastModified) {
    lastModified = Date.now();
  }
  const hash: string = md5.create().update(`${name}${lastModified.toString()}`).hex();
  return hash.substring(0, 10);
}

export function renameFile(file: File, newName: string): File {
  try {
    return new File([file], newName, { lastModified: file.lastModified, type: file.type });
  } catch {
    const newFile = new Blob([file], { type: file.type });
    return blobToFile(newFile, newName, file.lastModified);
  }
}

function blobToFile(blob: Blob, fileName: string, lastModified: number): File {
  const b: any = blob;
  b.lastModified = lastModified;
  b.name = fileName;
  return b as File;
}
